import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	CONTENT_CSV_LABELS,
	SetupWizardBooleanType,
	SetupWizardContent,
	ContentCsvKeys,
	ContentCsvTemplate,
	EMPTY_CONTENT_CSV_TEMPLATE,
	SetupWizardPayload,
	ContentType,
	TextUtils,
	ContentScoringMethod,
	SkillCode,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import {
	makeStatusColumn,
	SetupWizardBeforeAfterStatus,
} from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';
import { RubricService } from '../../../../../../../services/rubric.service';

const FULL_WIDTH_BREAKPOINT = 1510;

@Component({
	selector: 'app-setup-wizard-contents-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		NzTooltipModule,
		NzSpinModule,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardBeforeAfterStatus,
	],
	standalone: true,
	templateUrl: './setup-wizard-contents-table.html',
	styleUrl: './setup-wizard-contents-table.scss',
})
export class SetupWizardContentsTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public rubricCode = input.required<string | undefined>();
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public pageSize = input<number>(10);
	public rows = signal<SetupWizardContent[]>([]);
	readonly rowType = null! as BeforeAfter<ContentCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardContent>();
	public onToggleDisable = output<SetupWizardContent>();
	public onRemove = output<SetupWizardContent>();
	public onReset = output<SetupWizardContent>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public selectedRow = input<BeforeAfter<ContentCsvTemplate> | null>(null);
	public ContentCsvKeys = ContentCsvKeys;
	public CONTENT_CSV_LABELS = CONTENT_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private ssoContentIdFilterOptions: { text: string; value: string }[] = [];
	private ssoActivityIdFilterOptions: { text: string; value: string }[] = [];
	private displayOrderFilterOptions: { text: string; value: number }[] = [];
	private enNameFilterOptions: { text: string; value: string }[] = [];

	private ssoContentIdColumn: Column | undefined;
	private ssoActivityIdColumn: Column | undefined;
	private defaultLangIdColumn: Column | undefined;
	private displayOrderColumn: Column | undefined;
	private isInstructionColumn: Column | undefined;
	private contentTypeColumn: Column | undefined;
	private featuredModeColumn: Column | undefined;
	private scoringMethodColumn: Column | undefined;
	private skillCodeColumn: Column | undefined;
	private rubricLevelColumn: Column | undefined;
	private enNameColumn: Column | undefined;
	private enContentUriColumn: Column | undefined;
	private enContentTagsColumn: Column | undefined;
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard,
		private rubricService: RubricService
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.contents);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.ssoContentIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_content_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.ssoActivityIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_activity_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.displayOrderFilterOptions = [...new Set(rows.map(row => row.before?.display_order || 0))]
			.sort()
			.map(value => ({
				text: value.toString(),
				value: value,
			}));

		this.enNameFilterOptions = [...new Set(rows.map(row => row.before?.en_name || ''))].sort().map(value => ({
			text: value,
			value: value,
		}));

		if (this.ssoContentIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoContentIdColumn!.filterFn = undefined;
			this.ssoContentIdColumn!.filterOptions = undefined;
		} else {
			this.ssoContentIdColumn!.filterOptions = this.ssoContentIdFilterOptions;
			this.ssoContentIdColumn!.filterFn = (list: string[], item: SetupWizardContent) =>
				list.some(name => item.before?.sso_content_id?.indexOf(name) !== -1);
		}

		if (this.ssoActivityIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoActivityIdColumn!.filterFn = undefined;
			this.ssoActivityIdColumn!.filterOptions = undefined;
		} else {
			this.ssoActivityIdColumn!.filterOptions = this.ssoActivityIdFilterOptions;
			this.ssoActivityIdColumn!.filterFn = (list: string[], item: SetupWizardContent) =>
				list.some(name => item.before?.sso_activity_id?.indexOf(name) !== -1);
		}

		if (this.displayOrderFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.displayOrderColumn!.filterFn = undefined;
			this.displayOrderColumn!.filterOptions = undefined;
		} else {
			this.displayOrderColumn!.filterOptions = this.displayOrderFilterOptions;
			this.displayOrderColumn!.filterFn = (list: number[], item: SetupWizardContent) =>
				list.some(name => item.before?.display_order === name);
		}

		if (this.enNameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.enNameColumn!.filterFn = undefined;
			this.enNameColumn!.filterOptions = undefined;
		} else {
			this.enNameColumn!.filterOptions = this.enNameFilterOptions;
			this.enNameColumn!.filterFn = (list: string[], item: SetupWizardContent) =>
				list.some(name => item.before?.en_name?.indexOf(name) !== -1);
		}
	}

	public async initTable(): Promise<void> {
		try {
			await this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private async initColumns(): Promise<void> {
		const rubricCode = this.rubricCode();
		let levels: string[] = [];

		if (rubricCode) {
			levels = (await this.rubricService.getRubricLevels$(rubricCode)).map(level => level.level.toString());
		}

		Object.keys(EMPTY_CONTENT_CSV_TEMPLATE).map(key => {
			switch (key) {
				case ContentCsvKeys.ssoContentId:
					this.ssoContentIdColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.ssoContentId],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.sso_content_id!.localeCompare(b.before?.sso_content_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.ssoActivityId:
					this.ssoActivityIdColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.ssoActivityId],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.sso_activity_id!.localeCompare(b.before?.sso_activity_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.defaultLangId:
					this.defaultLangIdColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.defaultLangId],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.default_lang_id!.localeCompare(b.before?.default_lang_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.displayOrder:
					this.displayOrderColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.displayOrder],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							(a.before?.display_order ?? -1) - (b.before?.display_order ?? -1),
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.isInstruction:
					this.isInstructionColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.isInstruction],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							(a.after?.is_instruction ?? a.before?.is_instruction)
								?.toString()
								.localeCompare((b.after?.is_instruction ?? b.before?.is_instruction)?.toString()!) ??
							-1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(
								name =>
									(item.after?.is_instruction ?? item.before?.is_instruction)
										?.toString()
										?.indexOf(name) !== -1
							),
					};
					break;
				case ContentCsvKeys.contentType:
					this.contentTypeColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.contentType],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.content_type!.localeCompare(b.before?.content_type!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set(Object.values(ContentType))].map(value => ({
							text: TextUtils.convertSnakeCaseToTitleCase(value),
							value: value,
						})),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(name => (item.after?.content_type ?? item.before?.content_type) === name),
					};
					break;
				case ContentCsvKeys.featuredMode:
					this.featuredModeColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.featuredMode],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							(a.after?.featured_mode ?? a.before?.featured_mode)
								?.toString()
								.localeCompare((b.after?.featured_mode ?? b.before?.featured_mode)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(
								name =>
									(item.after?.featured_mode ?? item.before?.featured_mode)
										?.toString()
										?.indexOf(name) !== -1
							),
					};
					break;
				case ContentCsvKeys.scoringMethod:
					this.scoringMethodColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.scoringMethod],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.scoring_method!.localeCompare(b.before?.scoring_method!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set(Object.values(ContentScoringMethod))].map(value => ({
							text: TextUtils.convertSnakeCaseToTitleCase(value),
							value: value,
						})),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(name => (item.after?.scoring_method ?? item.before?.scoring_method) === name),
					};
					break;
				case ContentCsvKeys.skillCode:
					this.skillCodeColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.skillCode],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.skill_code!.localeCompare(b.before?.skill_code!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set(Object.values(SkillCode))].map(value => ({
							text: TextUtils.convertSnakeCaseToTitleCase(value),
							value: value,
						})),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(name => (item.after?.skill_code ?? item.before?.skill_code) === name),
					};
					break;
				case ContentCsvKeys.rubricLevel:
					this.rubricLevelColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.rubricLevel],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.rubric_level!.toString().localeCompare(b.before?.rubric_level?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set(levels)].map(value => ({
							text: TextUtils.convertSnakeCaseToTitleCase(value),
							value: value,
						})),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(
								name =>
									(item.after?.rubric_level?.toString() ?? item.before?.rubric_level?.toString()) ===
									name
							),
					};
					break;
				case ContentCsvKeys.enName:
					this.enNameColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.enName],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.en_name!.localeCompare(b.before?.en_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.enContentUri:
					this.enContentUriColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.enContentUri],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.en_content_uri!.localeCompare(b.before?.en_content_uri!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.enContentTags:
					this.enContentTagsColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.enContentTags],
						sortOrder: null,
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							a.before?.en_content_tags!.localeCompare(b.before?.en_content_tags!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ContentCsvKeys.active:
					this.activeColumn = {
						key,
						name: CONTENT_CSV_LABELS[ContentCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or false)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardContent, b: SetupWizardContent) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardContent) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					break;
			}
		});

		if (
			!this.ssoContentIdColumn ||
			!this.defaultLangIdColumn ||
			!this.displayOrderColumn ||
			!this.isInstructionColumn ||
			!this.contentTypeColumn ||
			!this.featuredModeColumn ||
			!this.scoringMethodColumn ||
			!this.skillCodeColumn ||
			!this.rubricLevelColumn ||
			!this.enNameColumn ||
			!this.enContentUriColumn ||
			!this.enContentTagsColumn ||
			!this.activeColumn
		) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardContent>();

		this.wideColumns = [
			this.ssoContentIdColumn,
			this.enNameColumn,
			this.displayOrderColumn,
			// this.isInstructionColumn,
			this.contentTypeColumn,
			this.featuredModeColumn,
			this.scoringMethodColumn,
			this.skillCodeColumn,
			this.rubricLevelColumn,
			this.enNameColumn,
			// this.enContentUriColumn,
			// this.enContentTagsColumn,
			this.activeColumn,
			statusColumn,
		];

		this.narrowColumns = [this.ssoContentIdColumn, this.enNameColumn, this.activeColumn];

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardContent) {
		this.onEdit.emit(row);
	}

	onToggleDisableClick(row: SetupWizardContent) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardContent) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardContent) {
		this.onReset.emit(row);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
